import {
  Box,
  Container,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  VStack,
  HStack,
  SimpleGrid,
  Select,
  useColorModeValue,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Flex,
  Badge,
  Progress,
  Icon,
  Divider,
  Tooltip,
  ButtonGroup,
  Button,
  Center,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToken,
  useColorMode,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from "../lib/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useState, useMemo } from "react";
import { colors } from "../assets/styles/colors";
import { useTranslation } from "react-i18next";
import {
  FiCalendar,
  FiBarChart2,
  FiPieChart,
  FiTrendingUp,
  FiBookOpen,
  FiCheckCircle,
  FiRepeat,
  FiGlobe,
  FiChevronDown,
  FiStar,
  FiTarget,
  FiActivity,
  FiAlertCircle,
} from "react-icons/fi";
import { keyframes } from "@emotion/react";
import logger from "../utils/logger";

const Analytics = () => {
  // Auth and utility hooks first - ALL useContext hooks must come first
  const { user } = useAuth();
  const { t } = useTranslation();

  // Add all other useContext hooks at the top level
  // These hooks were likely called conditionally somewhere
  const { colorMode } = useColorMode(); // Add useColorMode import if needed

  // State hooks
  const [timeRange, setTimeRange] = useState("7d");

  // Token hooks
  const [brandPrimary] = useToken("colors", ["brand.500"]);

  // Helper functions defined outside of hooks
  const getTimeRangeInMs = (range: string) => {
    switch (range) {
      case "7d":
        return 7 * 24 * 60 * 60 * 1000;
      case "30d":
        return 30 * 24 * 60 * 60 * 1000;
      case "90d":
        return 90 * 24 * 60 * 60 * 1000;
      default:
        return 7 * 24 * 60 * 60 * 1000;
    }
  };

  const getTimeRangeLabel = (range: string) => {
    switch (range) {
      case "7d":
        return t("analytics.last7Days");
      case "30d":
        return t("analytics.last30Days");
      case "90d":
        return t("analytics.last90Days");
      default:
        return t("analytics.last7Days");
    }
  };

  // Animation keyframes for hover effects
  const hoverAnimation = keyframes`
    0% { transform: translateY(0); }
    100% { transform: translateY(-8px); }
  `;

  const floatAnimation = keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-6px); }
    100% { transform: translateY(0px); }
  `;

  // Color values to match the new design language
  const cardBg = useColorModeValue("white", colors.primary[700]);
  const cardShadow = useColorModeValue(
    "0px 4px 12px rgba(0, 0, 0, 0.05)",
    "0px 4px 12px rgba(0, 0, 0, 0.2)"
  );
  const hoverCardShadow = useColorModeValue(
    "0px 8px 16px rgba(0, 0, 0, 0.1)",
    "0px 8px 16px rgba(0, 0, 0, 0.3)"
  );
  const borderColor = useColorModeValue("gray.200", colors.primary[600]);
  const textColor = useColorModeValue(colors.primary[800], "gray.100");
  const chartTextColor = useColorModeValue("#000000", "#ffffff");
  const chartGridColor = useColorModeValue("#e0e0e0", colors.primary[600]);
  const COLORS = [
    colors.accent.blue,
    colors.accent.green,
    colors.accent.purple,
    colors.accent.yellow,
  ];
  const mutedTextColor = useColorModeValue("gray.600", "gray.400");
  const iconColor = useColorModeValue(colors.primary[500], colors.primary[300]);
  const hoverBg = useColorModeValue("gray.50", colors.primary[600]);
  const tabBg = useColorModeValue("gray.50", colors.primary[600]);
  const tabSelectedBg = useColorModeValue("white", colors.primary[500]);
  const emptyStateBg = useColorModeValue(
    colors.primary[50],
    colors.primary[800]
  );

  // Pre-compute all color mode values that might be used conditionally
  const headerBgGradient = useColorModeValue(
    `linear(to-r, ${colors.accent.blue}, ${colors.accent.purple})`,
    `linear(to-r, ${colors.accent.blue}, ${colors.accent.purple})`
  );
  const iconBgColor = useColorModeValue(
    colors.primary[50],
    colors.primary[900]
  );
  const iconTextColor = useColorModeValue(
    colors.primary[600],
    colors.primary[200]
  );
  const buttonBgActive = useColorModeValue(
    colors.primary[500],
    colors.primary[500]
  );
  const buttonBgInactive = useColorModeValue("white", colors.primary[700]);
  const buttonTextActive = "white";
  const buttonTextInactive = useColorModeValue(colors.primary[700], "white");

  // Pre-compute all gradient values for stats
  const blueGradient = useColorModeValue(
    `linear(to-r, ${colors.accent.blue}, ${colors.primary[500]})`,
    `linear(to-r, ${colors.accent.blue}, ${colors.primary[400]})`
  );
  const greenGradient = useColorModeValue(
    `linear(to-r, ${colors.accent.green}, ${colors.primary[500]})`,
    `linear(to-r, ${colors.accent.green}, ${colors.primary[400]})`
  );
  const purpleGradient = useColorModeValue(
    `linear(to-r, ${colors.accent.purple}, ${colors.primary[500]})`,
    `linear(to-r, ${colors.accent.purple}, ${colors.primary[400]})`
  );
  const yellowGradient = useColorModeValue(
    `linear(to-r, ${colors.accent.yellow}, ${colors.primary[500]})`,
    `linear(to-r, ${colors.accent.yellow}, ${colors.primary[400]})`
  );

  // Compute box background colors for icons
  const blueBgLight = useColorModeValue(
    colors.accent.blue + "20",
    colors.accent.blue + "30"
  );
  const greenBgLight = useColorModeValue(
    colors.accent.green + "20",
    colors.accent.green + "30"
  );
  const purpleBgLight = useColorModeValue(
    colors.accent.purple + "20",
    colors.accent.purple + "30"
  );
  const yellowBgLight = useColorModeValue(
    colors.accent.yellow + "20",
    colors.accent.yellow + "30"
  );

  // Common card styles
  const statCardStyles = {
    bg: cardBg,
    borderWidth: "1px",
    borderColor: borderColor,
    borderRadius: "xl",
    overflow: "hidden",
    transition: "all 0.3s ease",
    boxShadow: cardShadow,
    _hover: {
      transform: "translateY(-8px)",
      boxShadow: hoverCardShadow,
    },
  };

  const iconContainerStyles = {
    borderRadius: "lg",
    p: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Query hooks
  const { data: sentences, isLoading: isSentencesLoading } = useQuery({
    queryKey: ["analytics-sentences", user?.id, timeRange],
    queryFn: async () => {
      const data = await apiClient.getSentences();
      const cutoffDate = new Date(
        Date.now() - getTimeRangeInMs(timeRange)
      ).toISOString();
      return data.filter((s: any) => s.created_at >= cutoffDate);
    },
    enabled: !!user,
  });

  // Add a separate count query for sentences to get accurate count
  const { data: sentencesCount } = useQuery({
    queryKey: ["analytics-sentences-count", user?.id],
    queryFn: async () => {
      try {
        const data = await apiClient.getSentences();
        return data.length;
      } catch (error) {
        logger.warn("Error fetching sentences count:", error);
        return 0;
      }
    },
    enabled: !!user,
  });

  const { data: words, isLoading: isWordsLoading } = useQuery({
    queryKey: ["analytics-words", user?.id],
    queryFn: async () => {
      // TODO: Add sentence count in API response
      const data = await apiClient.getWords();
      return data;
    },
    enabled: !!user,
  });

  // Add a separate count query for words to get accurate count
  const { data: wordsCount } = useQuery({
    queryKey: ["analytics-words-count", user?.id],
    queryFn: async () => {
      try {
        const data = await apiClient.getWords();
        return data.length;
      } catch (error) {
        logger.warn("Error fetching words count:", error);
        return 0;
      }
    },
    enabled: !!user,
  });

  // Use useMemo for stats calculation to avoid reference errors
  const stats = useMemo(() => {
    if (!sentences || !words) return null;

    // Use accurate counts from the count queries, falling back to array lengths
    const totalSentences = sentencesCount ?? sentences.length;
    const rememberedSentences = sentences.filter(
      (s: any) => s.remembered
    ).length;
    const totalWords = wordsCount ?? words.length;
    const averageTimesSeen =
      sentences.reduce((acc: number, s: any) => acc + s.times_seen, 0) /
      (sentences.length || 1);

    // Calculate stats by language
    const languageMap: Record<string, { count: number; remembered: number }> =
      {};
    sentences.forEach((sentence: any) => {
      const language = sentence.language || "unknown";
      if (!languageMap[language]) {
        languageMap[language] = { count: 0, remembered: 0 };
      }
      languageMap[language].count++;
      if (sentence.remembered) {
        languageMap[language].remembered++;
      }
    });

    const languageStats = Object.entries(languageMap).map(
      ([language, stats]) => ({
        language,
        count: stats.count,
        remembered: stats.remembered,
      })
    );

    // Format for pie chart
    const languagePieData = languageStats.map((stat) => ({
      name: stat.language.charAt(0).toUpperCase() + stat.language.slice(1),
      value: stat.count,
    }));

    // Calculate daily progress
    const dailyProgressMap: Record<
      string,
      { date: string; sentences: number; remembered: number }
    > = {};

    // Initialize with dates for the selected time range
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - getTimeRangeInMs(timeRange));

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      // Use ISO strings for consistent date handling
      const dateStr = d.toISOString().split("T")[0];
      dailyProgressMap[dateStr] = {
        date: dateStr,
        sentences: 0,
        remembered: 0,
      };
    }

    // fill in actual data
    sentences.forEach((sentence: any) => {
      if (!sentence.created_at) return;

      // Convert to ISO date string format YYYY-MM-DD
      const sentenceDate = new Date(sentence.created_at);
      const dateStr = sentenceDate.toISOString().split("T")[0];

      if (dailyProgressMap[dateStr]) {
        dailyProgressMap[dateStr].sentences++;
        if (sentence.remembered) {
          dailyProgressMap[dateStr].remembered++;
        }
      }
    });

    // Convert to array for chart
    const dailyProgress = Object.values(dailyProgressMap);

    return {
      totalSentences,
      rememberedSentences,
      totalWords,
      averageTimesSeen,
      languageStats,
      languagePieData,
      dailyProgress,
    };
  }, [sentences, words, timeRange, sentencesCount, wordsCount]);

  // UI event handlers
  const handleTimeRangeChange = (range: string) => {
    setTimeRange(range);
  };

  if (!user) {
    return (
      <Container maxW={{ base: "container.sm", md: "container.xl" }} py={10}>
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          shadow="md"
        >
          <CardBody>
            <VStack spacing={6} align="center" py={8}>
              <Box
                p={4}
                borderRadius="full"
                bg={colors.primary[50]}
                color={colors.primary[500]}
              >
                <Icon as={FiBarChart2} boxSize={12} />
              </Box>
              <Heading size="lg" color={textColor}>
                {t("analytics.title")}
              </Heading>
              <Text color={mutedTextColor}>{t("common.pleaseLogin")}</Text>
              <Button
                colorScheme="brand"
                size="lg"
                as="a"
                href="/login"
                leftIcon={<FiCheckCircle />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
                transition="all 0.2s"
              >
                {t("common.goToLogin")}
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (isSentencesLoading || isWordsLoading) {
    return (
      <Container maxW={{ base: "container.sm", md: "container.xl" }} py={10}>
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          shadow="md"
        >
          <CardBody>
            <Center py={10}>
              <VStack spacing={6}>
                <Spinner
                  size="xl"
                  thickness="4px"
                  color={colors.primary[500]}
                  speed="0.8s"
                />
                <Text color={textColor}>{t("common.loading")}</Text>
              </VStack>
            </Center>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (!stats || (stats.totalSentences === 0 && stats.totalWords === 0)) {
    return (
      <Container maxW={{ base: "container.sm", md: "container.xl" }} py={10}>
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          shadow="md"
        >
          <CardBody>
            <VStack spacing={6} align="center" py={8}>
              <Box
                p={4}
                borderRadius="full"
                bg={colors.accent.yellow + "20"}
                color={colors.accent.yellow}
              >
                <Icon as={FiAlertCircle} boxSize={12} />
              </Box>
              <Heading size="lg" color={textColor}>
                {t("analytics.noAnalyticsData")}
              </Heading>
              <Text color={mutedTextColor} maxW="md" textAlign="center">
                {t("analytics.practiceMore")}
              </Text>
              <ButtonGroup spacing={4}>
                <Button
                  colorScheme="brand"
                  size="lg"
                  as="a"
                  href="/play"
                  leftIcon={<FiActivity />}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                  }}
                  transition="all 0.2s"
                >
                  {t("common.practiceNow")}
                </Button>
                <Button
                  variant="outline"
                  colorScheme="brand"
                  size="lg"
                  as="a"
                  href="/dashboard"
                  leftIcon={<FiTarget />}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "sm",
                  }}
                  transition="all 0.2s"
                >
                  {t("common.dashboard")}
                </Button>
              </ButtonGroup>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      maxW={{ base: "container.sm", md: "container.xl" }}
      py={8}
      px={{ base: 4, md: 8 }}
    >
      <Heading
        as="h1"
        display="flex"
        alignItems="center"
        bgGradient="linear(to-l, #7928CA, #FF0080)"
        bgClip="text"
        fontWeight="extrabold"
        fontSize={{ base: "2xl", md: "3xl" }}
        mb={{ base: 6, md: 8 }}
        textAlign="left"
      >
        <Icon as={FiBarChart2} mr={2} />
        {t("analytics.title")}
      </Heading>

      <VStack spacing={8} align="stretch">
        {/* Time Range Selection */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          pb={4}
          borderBottom="1px solid"
          borderColor={borderColor}
          gap={4}
        >
          <Text color={mutedTextColor} fontSize="md">
            {getTimeRangeLabel(timeRange)}
          </Text>

          <ButtonGroup
            size="md"
            isAttached
            borderRadius="lg"
            overflow="hidden"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Button
              onClick={() => handleTimeRangeChange("7d")}
              colorScheme={timeRange === "7d" ? "brand" : undefined}
              variant={timeRange === "7d" ? "solid" : "outline"}
              leftIcon={<FiCalendar />}
              borderRightRadius={0}
              borderRightWidth={0}
              bg={timeRange === "7d" ? buttonBgActive : buttonBgInactive}
              color={timeRange === "7d" ? buttonTextActive : buttonTextInactive}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: timeRange === "7d" ? "none" : "sm",
              }}
              transition="all 0.3s"
            >
              7 {t("common.days")}
            </Button>
            <Button
              onClick={() => handleTimeRangeChange("30d")}
              colorScheme={timeRange === "30d" ? "brand" : undefined}
              variant={timeRange === "30d" ? "solid" : "outline"}
              borderRadius={0}
              borderRightWidth={0}
              borderLeftWidth={0}
              bg={timeRange === "30d" ? buttonBgActive : buttonBgInactive}
              color={
                timeRange === "30d" ? buttonTextActive : buttonTextInactive
              }
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: timeRange === "30d" ? "none" : "sm",
              }}
              transition="all 0.3s"
            >
              30 {t("common.days")}
            </Button>
            <Button
              onClick={() => handleTimeRangeChange("90d")}
              colorScheme={timeRange === "90d" ? "brand" : undefined}
              variant={timeRange === "90d" ? "solid" : "outline"}
              borderLeftRadius={0}
              borderLeftWidth={0}
              bg={timeRange === "90d" ? buttonBgActive : buttonBgInactive}
              color={
                timeRange === "90d" ? buttonTextActive : buttonTextInactive
              }
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: timeRange === "90d" ? "none" : "sm",
              }}
              transition="all 0.3s"
            >
              90 {t("common.days")}
            </Button>
          </ButtonGroup>
        </Flex>

        {/* Key Metrics */}
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          overflow="hidden"
          transition="all 0.3s ease"
          boxShadow={cardShadow}
          position="relative"
          mb={6}
          _hover={{
            transform: "translateY(-4px)",
            boxShadow: "xl",
          }}
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="4px"
            bgGradient={`linear(to-r, ${colors.accent.blue}, ${colors.accent.purple})`}
          />
          <CardBody py={4}>
            <SimpleGrid
              columns={{ base: 1, sm: 2, md: 4 }}
              spacing={{ base: 4, md: 0 }}
            >
              <HStack
                align="center"
                justify="center"
                px={3}
                borderRight={{ base: "none", md: "1px solid" }}
                borderColor={borderColor}
                borderBottom={{ base: "1px solid", md: "none" }}
                pb={{ base: 3, md: 0 }}
                mb={{ base: 2, md: 0 }}
                spacing={2}
              >
                <Box
                  bg={blueBgLight}
                  p={2}
                  borderRadius="md"
                  color={colors.accent.blue}
                >
                  <Icon as={FiBookOpen} boxSize={4} />
                </Box>
                <Text fontSize="sm" fontWeight="medium" color={mutedTextColor}>
                  {t("analytics.totalWords")}:
                </Text>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  bgGradient={blueGradient}
                  bgClip="text"
                >
                  {stats.totalWords.toLocaleString()}
                </Text>
              </HStack>

              <HStack
                align="center"
                justify="center"
                px={3}
                borderRight={{ base: "none", md: "1px solid" }}
                borderColor={borderColor}
                borderBottom={{ base: "1px solid", md: "none" }}
                pb={{ base: 3, md: 0 }}
                mb={{ base: 2, md: 0 }}
                spacing={2}
              >
                <Box
                  bg={greenBgLight}
                  p={2}
                  borderRadius="md"
                  color={colors.accent.green}
                >
                  <Icon as={FiCheckCircle} boxSize={4} />
                </Box>
                <Text fontSize="sm" fontWeight="medium" color={mutedTextColor}>
                  {t("analytics.totalSentences")}:
                </Text>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  bgGradient={greenGradient}
                  bgClip="text"
                >
                  {stats.totalSentences.toLocaleString()}
                </Text>
              </HStack>

              <HStack
                align="center"
                justify="center"
                px={3}
                borderRight={{ base: "none", md: "1px solid" }}
                borderColor={borderColor}
                borderBottom={{ base: "1px solid", md: "none" }}
                pb={{ base: 3, md: 0 }}
                mb={{ base: 2, md: 0 }}
                spacing={2}
              >
                <Box
                  bg={purpleBgLight}
                  p={2}
                  borderRadius="md"
                  color={colors.accent.purple}
                >
                  <Icon as={FiStar} boxSize={4} />
                </Box>
                <Text fontSize="sm" fontWeight="medium" color={mutedTextColor}>
                  {t("analytics.remembered")}:
                </Text>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  bgGradient={purpleGradient}
                  bgClip="text"
                >
                  {stats.rememberedSentences.toLocaleString()}
                  <Text as="span" fontSize="xs" color={mutedTextColor} ml={1}>
                    (
                    {stats.totalSentences > 0
                      ? `${Math.round(
                          (stats.rememberedSentences / stats.totalSentences) *
                            100
                        )}%`
                      : "0%"}
                    )
                  </Text>
                </Text>
              </HStack>

              <HStack align="center" justify="center" px={3} spacing={2}>
                <Box bg={yellowBgLight} p={1} borderRadius="md">
                  <Icon as={FiRepeat} boxSize={4} />
                </Box>
                <Text fontSize="sm" fontWeight="medium" color={mutedTextColor}>
                  {t("analytics.avgTimesSeen")}:
                </Text>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  bgGradient={yellowGradient}
                  bgClip="text"
                >
                  {stats.averageTimesSeen.toFixed(1)}
                </Text>
              </HStack>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Charts section */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mt={4}>
          {/* Daily Progress Chart */}
          <Card
            {...statCardStyles}
            minH="400px"
            transition="all 0.3s"
            _hover={{
              transform: "translateY(-8px)",
              boxShadow: "xl",
            }}
          >
            <CardHeader pb={2}>
              <HStack spacing={3}>
                <Box
                  bg={blueBgLight}
                  p={2}
                  borderRadius="md"
                  color={colors.accent.blue}
                >
                  <Icon as={FiTrendingUp} boxSize={5} />
                </Box>
                <Heading size="md">{t("analytics.dailyProgress")}</Heading>
              </HStack>
            </CardHeader>
            <CardBody pt={2}>
              {stats.dailyProgress.length > 0 ? (
                <Box h="300px">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={stats.dailyProgress}
                      margin={{
                        top: 20,
                        right: 20,
                        left: 20,
                        bottom: 20,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={chartGridColor}
                      />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value) => {
                          try {
                            // Parse ISO date string (YYYY-MM-DD)
                            const date = new Date(value);
                            if (isNaN(date.getTime())) {
                              return "Invalid";
                            }
                            return date.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            });
                          } catch (error) {
                            logger.error("Date parsing error:", error, value);
                            return "Invalid";
                          }
                        }}
                        stroke={chartTextColor}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis stroke={chartTextColor} tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: cardBg,
                          borderColor: borderColor,
                          borderRadius: "8px",
                          boxShadow: cardShadow,
                        }}
                        labelStyle={{ color: textColor }}
                        formatter={(value, name) => [
                          value,
                          name === "sentences"
                            ? t("common.sentences")
                            : t("analytics.remembered"),
                        ]}
                        labelFormatter={(label) => {
                          try {
                            // Parse ISO date string (YYYY-MM-DD)
                            const date = new Date(label);
                            if (isNaN(date.getTime())) {
                              return "Invalid date";
                            }
                            return date.toLocaleDateString();
                          } catch (error) {
                            logger.error(
                              "Tooltip date parsing error:",
                              error,
                              label
                            );
                            return "Invalid date";
                          }
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="sentences"
                        stackId="1"
                        stroke={colors.accent.blue}
                        fill={colors.accent.blue + "40"}
                        name={t("common.sentences")}
                        animationDuration={1500}
                        animationEasing="ease-in-out"
                      />
                      <Area
                        type="monotone"
                        dataKey="remembered"
                        stackId="2"
                        stroke={colors.accent.green}
                        fill={colors.accent.green + "40"}
                        name={t("analytics.remembered")}
                        animationDuration={1500}
                        animationEasing="ease-in-out"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Center h="300px" bg={emptyStateBg} borderRadius="lg">
                  <VStack spacing={3}>
                    <Icon
                      as={FiTrendingUp}
                      boxSize={10}
                      color={mutedTextColor}
                    />
                    <Text color={mutedTextColor}>
                      {t("analytics.noDataForTimeRange")}
                    </Text>
                  </VStack>
                </Center>
              )}
            </CardBody>
          </Card>

          {/* Language Distribution */}
          <Card
            {...statCardStyles}
            minH="400px"
            transition="all 0.3s"
            _hover={{
              transform: "translateY(-8px)",
              boxShadow: "xl",
            }}
          >
            <CardHeader pb={2}>
              <HStack spacing={3}>
                <Box
                  bg={greenBgLight}
                  p={2}
                  borderRadius="md"
                  color={colors.accent.green}
                >
                  <Icon as={FiGlobe} boxSize={5} />
                </Box>
                <Heading size="md">
                  {t("analytics.languageDistribution")}
                </Heading>
              </HStack>
            </CardHeader>
            <CardBody pt={2}>
              {stats.languagePieData && stats.languagePieData.length > 0 ? (
                <Box h="300px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.languagePieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={{ stroke: chartTextColor, strokeWidth: 1 }}
                        animationDuration={1500}
                        animationEasing="ease-in-out"
                      >
                        {stats.languagePieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: cardBg,
                          borderColor: borderColor,
                          borderRadius: "8px",
                          boxShadow: cardShadow,
                        }}
                        formatter={(value, name, props) => [
                          `${value} ${t("common.sentences")}`,
                          props.payload.name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Center h="300px" bg={emptyStateBg} borderRadius="lg">
                  <VStack spacing={3}>
                    <Icon as={FiGlobe} boxSize={10} color={mutedTextColor} />
                    <Text color={mutedTextColor}>
                      {t("analytics.noLanguageData")}
                    </Text>
                  </VStack>
                </Center>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Learning Rate Chart */}
        <Card
          {...statCardStyles}
          mt={4}
          transition="all 0.3s"
          _hover={{
            transform: "translateY(-8px)",
            boxShadow: "xl",
          }}
        >
          <CardHeader pb={2}>
            <HStack spacing={3}>
              <Box
                bg={purpleBgLight}
                p={2}
                borderRadius="md"
                color={colors.accent.purple}
              >
                <Icon as={FiTarget} boxSize={5} />
              </Box>
              <Heading size="md">{t("analytics.learningRate")}</Heading>
            </HStack>
          </CardHeader>
          <CardBody pt={2}>
            {stats.languageStats && stats.languageStats.length > 0 ? (
              <Box h="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.languageStats}
                    margin={{
                      top: 20,
                      right: 20,
                      left: 20,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartGridColor}
                    />
                    <XAxis
                      dataKey="language"
                      stroke={chartTextColor}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) =>
                        value.charAt(0).toUpperCase() + value.slice(1)
                      }
                    />
                    <YAxis stroke={chartTextColor} tick={{ fontSize: 12 }} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: cardBg,
                        borderColor: borderColor,
                        borderRadius: "8px",
                        boxShadow: cardShadow,
                      }}
                      formatter={(value, name) => [
                        value,
                        name === "count"
                          ? t("common.sentences")
                          : t("analytics.remembered"),
                      ]}
                      labelFormatter={(label) =>
                        label.charAt(0).toUpperCase() + label.slice(1)
                      }
                    />
                    <Bar
                      dataKey="count"
                      fill={colors.accent.blue}
                      radius={[4, 4, 0, 0]}
                      name={t("common.sentences")}
                      animationDuration={1500}
                      animationEasing="ease-in-out"
                    />
                    <Bar
                      dataKey="remembered"
                      fill={colors.accent.green}
                      radius={[4, 4, 0, 0]}
                      name={t("analytics.remembered")}
                      animationDuration={1500}
                      animationEasing="ease-in-out"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Center h="300px" bg={emptyStateBg} borderRadius="lg">
                <VStack spacing={3}>
                  <Icon as={FiBarChart2} boxSize={10} color={mutedTextColor} />
                  <Text color={mutedTextColor}>
                    {t("analytics.noDataForTimeRange")}
                  </Text>
                </VStack>
              </Center>
            )}
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default Analytics;
